// The ad copy values ticked for an Advanced draft, and what they make (live's `text-copies-modal`
// `utils.ts` and `useSavedCopiesSelections`): per tile (a reference, or the Style), per copy, per
// section, the ticked values and a placement. Their combinations are the job's images (G8).
import type {
  AdReference,
  AdTextPlacements,
  AdTextValues,
  AdTextVariation,
} from "../../../api/generated/model";
import { SECTIONS, type TextKey } from "./elements";

/** A section's ticked values, and where the image puts them. */
export interface KeyPick {
  values: string[];
  placement?: string;
}

export type CopyPicks = Partial<Record<TextKey, KeyPick>>;
/** A tile's ticks, by copy id. */
export type TilePicks = Record<string, CopyPicks>;
/** The draft's ticks, by tile: a reference's asset id, or `STYLE_TILE`. */
export type Picks = Record<string, TilePicks>;

/** The tile of a draft with no reference: the copies follow the Style (live's `"style"` key). */
export const STYLE_TILE = "style";

/** Live's `MAX_SELECTED_COUNT`, G8's cap on an Advanced job's images. */
export const MAX_COMBINATIONS = 100;

/** One copy's images on a tile: the product of its ticked sections' counts. */
export function copyCombinations(copy: CopyPicks | undefined): number {
  const counts = Object.values(copy ?? {})
    .map((pick) => pick?.values.length ?? 0)
    .filter((count) => count > 0);
  return counts.length === 0 ? 0 : counts.reduce((product, count) => product * count, 1);
}

/** A tile's images: its copies' combinations, summed. */
export function tileCombinations(tile: TilePicks | undefined): number {
  return Object.values(tile ?? {}).reduce((sum, copy) => sum + copyCombinations(copy), 0);
}

/** The draft's images: its tiles' combinations, summed. */
export function totalCombinations(picks: Picks): number {
  return Object.values(picks).reduce((sum, tile) => sum + tileCombinations(tile), 0);
}

/** `picks` with one section's values replaced; an emptied section, copy or tile goes. */
function withValues(
  picks: Picks,
  at: { tile: string; copyId: string; key: TextKey },
  values: (current: string[]) => string[],
): Picks {
  const tile = { ...picks[at.tile] };
  const copy = { ...tile[at.copyId] };
  const current = copy[at.key];
  const next = values(current?.values ?? []);
  if (next.length > 0) copy[at.key] = { ...current, values: next };
  else delete copy[at.key];
  if (Object.keys(copy).length > 0) tile[at.copyId] = copy;
  else delete tile[at.copyId];
  const result = { ...picks };
  if (Object.keys(tile).length > 0) result[at.tile] = tile;
  else delete result[at.tile];
  return result;
}

/** Ticks or unticks one value (live's `handleToggleValue`). */
export function toggleValue(
  picks: Picks,
  at: { tile: string; copyId: string; key: TextKey },
  value: string,
): Picks {
  return withValues(picks, at, (current) =>
    current.includes(value) ? current.filter((each) => each !== value) : [...current, value],
  );
}

/** Ticks all of a section's values when none is ticked, else none (live's `handleToggleAll`). */
export function toggleAll(
  picks: Picks,
  at: { tile: string; copyId: string; key: TextKey },
  values: readonly string[],
): Picks {
  return withValues(picks, at, (current) => (current.length === 0 ? [...values] : []));
}

/** Sets a ticked section's placement; a section with no tick takes none. */
export function setPlacement(
  picks: Picks,
  at: { tile: string; copyId: string; key: TextKey },
  placement: string,
): Picks {
  const current = picks[at.tile]?.[at.copyId]?.[at.key];
  if (current === undefined) return picks;
  return {
    ...picks,
    [at.tile]: {
      ...picks[at.tile],
      [at.copyId]: { ...picks[at.tile]![at.copyId], [at.key]: { ...current, placement } },
    },
  };
}

/** The ticks of the tiles still there (live's opening of the modal, a reference removed). */
export function keepTiles(picks: Picks, tiles: readonly string[]): Picks {
  return Object.fromEntries(Object.entries(picks).filter(([tile]) => tiles.includes(tile)));
}

/** One copy's variation: its ticked values and their placements (live's `buildUserTextVariation`). */
export function variationOf(copy: CopyPicks): AdTextVariation | null {
  const values: AdTextValues = {};
  const placements: AdTextPlacements = {};
  for (const { key } of SECTIONS) {
    const pick = copy[key];
    const ticked = (pick?.values ?? []).filter((value) => value.trim() !== "");
    if (ticked.length === 0) continue;
    values[key] = ticked;
    const placement = pick?.placement?.trim() ?? "";
    if (placement !== "") placements[`${key}_placement`] = placement;
  }
  if (Object.keys(values).length === 0) return null;
  return Object.keys(placements).length === 0 ? { values } : { values, placements };
}

function variationsOf(tile: TilePicks | undefined): AdTextVariation[] {
  return Object.values(tile ?? {})
    .map(variationOf)
    .filter((variation) => variation !== null);
}

/**
 * The job's references (live's `buildReferenceParams`): each reference with ticks names its image
 * and has a variation per ticked copy, in the draft's order; with no reference, one reference
 * follows the Style. A tile with no tick sends nothing.
 */
export function advancedReferences(picks: Picks, references: readonly string[]): AdReference[] {
  if (references.length === 0) {
    const variations = variationsOf(picks[STYLE_TILE]);
    return variations.length === 0 ? [] : [{ variations }];
  }
  return references.flatMap((assetId) => {
    const variations = variationsOf(picks[assetId]);
    return variations.length === 0
      ? []
      : [{ source: { kind: "asset" as const, asset_id: assetId }, variations }];
  });
}
